import { useCallback, useRef } from 'react';

import {
  isValidEnumValue,
  setQueryParamSilently,
  usePersistentState,
} from '@cast/utils';

import { LIST_PRICE_QUERY_PARAM } from 'features/cost-report/constants';
import { CostReportPriceType } from 'features/cost-report/types/common';
import { readStorage, writeStorage } from 'utils/storage';

type Result = {
  priceType: CostReportPriceType;
  updatePriceType(value: CostReportPriceType): void;
};

export const STORAGE_KEY = 'cost-report.price-type';

export const useCostReportPriceType = (): Result => {
  const initializedRef = useRef(false);

  if (!initializedRef.current) {
    initializedRef.current = true;

    // URL has highest priority — write it to storage so usePersistentState picks it up
    const rawUrlValue = new URLSearchParams(window.location.search).get(
      LIST_PRICE_QUERY_PARAM
    );
    if (isValidEnumValue(CostReportPriceType, rawUrlValue)) {
      writeStorage(STORAGE_KEY, rawUrlValue);
    }

    const storedValue = readStorage<CostReportPriceType>(
      STORAGE_KEY,
      CostReportPriceType.DEFAULT
    );

    if (!isValidEnumValue(CostReportPriceType, storedValue)) {
      writeStorage(STORAGE_KEY, CostReportPriceType.DEFAULT);
    }
  }

  const [priceType, setPriceType] = usePersistentState<CostReportPriceType>(
    STORAGE_KEY,
    CostReportPriceType.DEFAULT
  );

  const updatePriceType = useCallback(
    (value: CostReportPriceType) => {
      setPriceType(value);
      setQueryParamSilently(LIST_PRICE_QUERY_PARAM, value);
    },
    [setPriceType]
  );

  return {
    priceType,
    updatePriceType,
  };
};
